import { useState, useEffect } from 'react';
import { Handshake, PlayCircle } from 'lucide-react';
import ProductDemoModal from '@/components/Common/ProductDemoModal';
import FillerModal from '@/components/Fillermodal';
import { fetchFeaturedVideo } from '@/services/featuredVideoService';

import camoTexture from '@assets/images/Hero/hero-bg.png';

export default function Hero() {
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [isFillerOpen, setIsFillerOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [sourceType, setSourceType] = useState<'youtube' | 'upload' | null>(
    null,
  );
  const [loadingVideo, setLoadingVideo] = useState(true);

  // =============================
  // PARALLAX SCROLL EFFECT
  // =============================
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // =============================
  // LOAD FEATURED VIDEO
  // =============================
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchFeaturedVideo();
        if (data?.videoUrl) {
          setVideoUrl(data.videoUrl);
          setSourceType(data.source);
        }
      } catch (err) {
        console.error('Failed to load featured video:', err);
      } finally {
        setLoadingVideo(false);
      }
    }
    load();
  }, []);

  // =============================
  // BACKGROUND VIDEO RENDERER
  // =============================
  const renderBackgroundVideo = () => {
    if (loadingVideo || !videoUrl) return null;

    if (sourceType === 'youtube') {
      const ytId = videoUrl.split('/').pop();
      return (
        <iframe
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            transition: 'transform 0.1s ease-out',
          }}
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&controls=0&playlist=${ytId}&modestbranding=1&showinfo=0&rel=0&disablekb=1`}
          allow="autoplay; encrypted-media"
        />
      );
    }

    return (
      <video
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          transform: `translateY(${scrollY * 0.5}px)`,
          transition: 'transform 0.1s ease-out',
        }}
        src={videoUrl}
        autoPlay
        loop
        muted
        playsInline
      />
    );
  };

  return (
    <>
      <section className="relative min-h-screen w-full overflow-hidden text-white">
        {/* === 1: VIDEO BACKGROUND === */}
        <div className="absolute inset-0 z-0">{renderBackgroundVideo()}</div>

        {/* === 2: BLACK GRADIENT LEFT TO RIGHT === */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/80 via-black/50 to-transparent" />

        {/* === 3: CAMOUFLAGE OVERLAY === */}
        <div
          className="absolute inset-0 z-[2] opacity-[0.30] mix-blend-overlay"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.25), rgba(0,0,0,0)), url(${camoTexture})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'repeat',
          }}
        />

        {/* === 4: DARK GRADIENT OVERLAY === */}
        <div className="absolute inset-0 z-[3] bg-gradient-to-b from-black/10 via-black/40 to-black/80" />

        {/* === 5: MAIN CONTENT === */}
        <div className="relative  z-[4] mx-auto flex min-h-screen max-w-[1500px] flex-col items-center justify-center px-4 pb-16 pt-24 sm:px-6 sm:pb-20 sm:pt-28 md:flex-row md:justify-between md:px-8 md:pt-32 lg:px-16 xl:px-28 2xl:px-32">
          {/* LEFT TEXT BLOCK */}
          <div
            data-aos="fade-right"
            className="flex w-full flex-col space-y-4 text-center sm:space-y-5 md:w-[55%] md:space-y-6 md:text-left lg:w-[50%]"
          >
            <h1 className="sm:text-3x text-2xl font-extrabold leading-tight tracking-tight drop-shadow-[0_3px_12px_rgba(0,0,0,0.9)] md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-5xl">
              The <span className="text-[#FFBD59]">Leading Innovator</span>
              <br /> in Ballistic Protection
              <br /> in the Philippines
            </h1>

            <p className="text-xs leading-relaxed text-gray-200 drop-shadow-[0_3px_10px_rgba(0,0,0,0.90)] sm:text-sm md:text-base 2xl:text-xl">
              <span className="font-semibold text-[#FFBD59]">AeroComp</span>{' '}
              develops locally made, high-performance ballistic armor using
              advanced hybrid fiber-reinforced composites—empowering the
              Philippines toward defense self-sufficiency.
            </p>

            {/* BUTTONS */}
            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-3 md:gap-4">
              <button
                onClick={() => window.open('/appointmentsystem', '_blank')}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#FFBD59] px-4 py-3 text-xs font-medium text-black transition-all hover:bg-[#FFBD59]/90 sm:gap-3 sm:px-5 sm:py-3.5 sm:text-sm md:px-6 md:py-4 md:text-base lg:gap-4 lg:px-7 lg:text-base 2xl:px-8 2xl:py-4 2xl:text-lg"
              >
                <Handshake size={18} className="sm:h-5 sm:w-5" /> Connect with
                us
              </button>

              <button
                onClick={() => setIsDemoOpen(true)}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#FFBD59] px-4 py-3 text-xs font-semibold text-[#FFBD59] transition hover:bg-[#FFBD59] hover:text-black sm:gap-3 sm:px-5 sm:py-3.5 sm:text-sm md:px-6 md:py-4 md:text-base lg:px-7 lg:text-base 2xl:px-8 2xl:py-4 2xl:text-lg"
              >
                <PlayCircle size={18} className="sm:h-5 sm:w-5" /> Watch More
              </button>
            </div>
          </div>

          {/* RIGHT SPACER */}
          <div className="hidden w-[45%] md:block lg:w-[50%]"></div>
        </div>
      </section>

      {/* === MODALS === */}

      {/* 👉 FEATURED VIDEO MODAL (INTEGRATED) */}
      <ProductDemoModal
        isOpen={isDemoOpen}
        onClose={() => setIsDemoOpen(false)}
        videoUrl={videoUrl}
        source={sourceType}
      />

      {/* 👉 FILLER MODAL */}
      <FillerModal
        isOpen={isFillerOpen}
        onClose={() => setIsFillerOpen(false)}
        title="Ongoing Development!"
        message="Our APPOINTMENT SYSTEM is still being finalized. For inquiries, email us at"
        email=" aerocompproject@gmail.com"
        primaryLabel="Send us an Email"
        secondaryLabel="Go Back"
      />
    </>
  );
}
